import { applyGhostStyle } from '../../plugin-sdk/ghost_preview.mjs';
import {
  clearInteractionFlag,
  clearInteractionTimer,
  getInteractionFlag
} from '../interaction/interaction_transient_state.js?v=20260904f';
import {
  buildObstacleSegmentConnectionGraph,
  buildPipeConnectionGraph,
  buildPipeRenderSegments,
  buildRectangularObstacleRenderSegments,
  ensureObstacleState,
  normalizeObstacle,
  obstacleActiveTool,
  obstacleById,
  obstacleCenter,
  obstaclePlanRadius,
  pendingObstacleConfig,
  rotateObstacle,
  selectObstacle,
  selectedObstacle,
  startObstacleRotatePress
} from './obstacles.js?v=20260903c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import {
  getPlanSvg,
  setStatus,
  stopCanvasMouseDown,
  svgEl,
  worldToScreen
} from '../render/plan_renderer.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import {
  PLAN_LAYER_IDS,
  isPlanLayerVisible,
  planLayerSvgAttributes
} from '../render/plan_layer_service.js?v=20260814b';

const OBSTACLE_STYLE = {
  pipe: {
    stroke: '#555555',
    fill: 'rgba(120, 120, 120, 0.4)'
  },
  ventilation: {
    stroke: '#AAAAAA',
    fill: 'rgba(180, 180, 180, 0.3)'
  }
};

/**
 * Rendert draw Obstacles2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawObstacles2D(parent) {
  if (!parent) return;
  ensureObstacleState();
  const selectedIds = new Set(
    Array.isArray(S.selection?.obstacleIds) && S.selection.obstacleIds.length
      ? S.selection.obstacleIds
      : S.selection?.obstacleId
        ? [S.selection.obstacleId]
      : []
  );
  const layer = svgEl('g', { class: 'obstacle-layer' }, parent);
  const ventilationLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.OBSTACLES_VENTILATION,
    S.project,
    { role: 'content', attrs: { class: 'obstacle-ventilation-plan-layer' } }
  ), layer);
  const pipeLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.OBSTACLES_PIPES,
    S.project,
    { role: 'content', attrs: { class: 'obstacle-pipe-plan-layer' } }
  ), layer);
  const ventilationVisible = isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_VENTILATION);
  const pipesVisible = isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_PIPES);
  const pipeOptions = new Map();
  const ductOptions = new Map();
  const pipes = [];
  const ducts = [];
  for (const obstacle of S.project.obstacles) {
    if (obstacle.type === 'pipe') {
      pipes.push(obstacle);
      pipeOptions.set(obstacle.id, {
        selected: selectedIds.has(obstacle.id)
      });
      continue;
    }
    if (obstacle.type === 'duct') {
      ducts.push(obstacle);
      ductOptions.set(obstacle.id, {
        selected: selectedIds.has(obstacle.id)
      });
      continue;
    }
    if (ventilationVisible) {
      drawObstacle2D(ventilationLayer, obstacle, {
        selected: selectedIds.has(obstacle.id)
      });
    }
  }
  if (ventilationVisible && ducts.length) renderDuctNetwork2D(ventilationLayer, ducts, ductOptions);
  if (pipesVisible && pipes.length) renderPipeNetwork2D(pipeLayer, pipes, pipeOptions);
}

/**
 * Rendert draw Obstacle2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} obstacle - Eingabewert für draw Obstacle2 D.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawObstacle2D(parent, obstacle, options = {}) {
  if (obstacle.type === 'pipe') {
    drawPipeObstacle2D(parent, obstacle, options);
    return;
  }
  if (obstacle.type === 'duct') {
    drawDuctObstacle2D(parent, obstacle, options);
    return;
  }
  const start = worldToScreen(obstacle.start.x, obstacle.start.z);
  const end = worldToScreen(obstacle.end.x, obstacle.end.z);
  const scale = Math.max(0.05, S.camera2D.scale || 1);
  const radiusMm = obstaclePlanRadius(obstacle);
  const strokeWidth = Math.max(5, Math.min(42, radiusMm * 2 * scale));
  const selected = Boolean(options.selected);
  const color = obstacle.type === 'pipe'
    ? OBSTACLE_STYLE.pipe.stroke
    : OBSTACLE_STYLE.ventilation.stroke;
  const soft = obstacle.type === 'pipe'
    ? OBSTACLE_STYLE.pipe.fill
    : OBSTACLE_STYLE.ventilation.fill;
  const label = obstacle.type === 'pipe'
    ? `Rohr Ø ${Math.round(obstacle.diameter)}`
    : `Lüftung ${Math.round(obstacle.width)} x ${Math.round(obstacle.height)}`;

  if (selected) {
    svgEl('line', {
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      stroke: '#f59d13',
      'stroke-width': strokeWidth + 10,
      'stroke-linecap': obstacle.type === 'pipe' ? 'round' : 'butt',
      opacity: 0.28,
      'pointer-events': 'none'
    }, parent);
  }

  svgEl('line', {
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
    stroke: soft,
    'stroke-width': strokeWidth + 5,
    'stroke-linecap': obstacle.type === 'pipe' ? 'round' : 'butt',
    'pointer-events': 'none'
  }, parent);
  svgEl('line', {
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
    stroke: color,
    'stroke-width': Math.max(2, strokeWidth),
    'stroke-linecap': obstacle.type === 'pipe' ? 'round' : 'butt',
    'stroke-dasharray': obstacle.type === 'duct' ? `${Math.max(8, strokeWidth * 0.6)} ${Math.max(5, strokeWidth * 0.35)}` : '',
    opacity: 0.78,
    'pointer-events': 'none'
  }, parent);
  svgEl('line', {
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
    stroke: '#ffffff',
    'stroke-width': Math.max(1.5, Math.min(4, strokeWidth * 0.16)),
    'stroke-linecap': obstacle.type === 'pipe' ? 'round' : 'butt',
    opacity: 0.9,
    'pointer-events': 'none'
  }, parent);

  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  svgEl('text', {
    x: mid.x + 8,
    y: mid.y - 8,
    fill: '#334155',
    'font-size': 12,
    'font-weight': 700,
    'pointer-events': 'none'
  }, parent).textContent = label;
}

/**
 * Rendert draw Pipe Obstacle2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} obstacle - Eingabewert für draw Pipe Obstacle2 D.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawPipeObstacle2D(parent, obstacle, options = {}) {
  renderPipeNetwork2D(parent, [obstacle], new Map([[obstacle.id, options]]));
}

function renderPipeNetwork2D(parent, pipes = [], optionsById = new Map()) {
  if (!parent || !Array.isArray(pipes) || !pipes.length) return;
  const normalizedPipes = pipes
    .map(pipe => {
      const normalized = normalizeObstacle(pipe);
      if (normalized) normalized.__renderOptions = optionsById.get(pipe?.id) || optionsById.get(normalized.id) || {};
      return normalized;
    })
    .filter(pipe => pipe?.type === 'pipe' && obstacleSegmentLength2D(pipe) > 0.001);
  if (!normalizedPipes.length) return;
  const scale = Math.max(0.05, S.camera2D.scale || 1);
  const graph = buildPipeConnectionGraph(normalizedPipes);
  const joints = Array.isArray(graph.joints) ? graph.joints : [];
  const visualPipes = buildPipeRenderSegments(normalizedPipes);
  const normalizedOptionsById = new Map(optionsById);
  normalizedPipes.forEach(pipe => normalizedOptionsById.set(pipe.id, pipe.__renderOptions || {}));
  const highlightLayer = svgEl('g', { class: 'pipe-highlight-layer' }, parent);
  const bodyLayer = svgEl('g', { class: 'pipe-body-layer' }, parent);
  const centerLayer = svgEl('g', { class: 'pipe-centerline-layer' }, parent);
  const jointLayer = svgEl('g', { class: 'pipe-joint-layer' }, parent);
  const labelLayer = svgEl('g', { class: 'pipe-label-layer' }, parent);

  normalizedPipes.forEach(pipe => {
    const options = pipe.__renderOptions || optionsById.get(pipe.id) || {};
    drawPipeSegment2D(highlightLayer, pipe, scale, options, 'highlight');
  });
  visualPipes.forEach(pipe => {
    const options = pipeRenderSegmentOptions(pipe, normalizedOptionsById);
    drawPipeSegment2D(bodyLayer, pipe, scale, options, 'body');
  });
  visualPipes.forEach(pipe => drawPipeSegment2D(centerLayer, pipe, scale, {}, 'center'));
  drawPipeJoints2D(jointLayer, joints, normalizedPipes, normalizedOptionsById, scale);
  normalizedPipes.forEach(pipe => drawPipeLabel2D(labelLayer, pipe));
}

function pipeRenderSegmentOptions(pipe, optionsById = new Map()) {
  const ids = Array.isArray(pipe?.sourcePipeIds) && pipe.sourcePipeIds.length
    ? pipe.sourcePipeIds
    : pipe?.id
      ? [pipe.id]
      : [];
  return {
    selected: ids.some(id => optionsById.get(id)?.selected)
  };
}

function drawPipeSegment2D(parent, pipe, scale, options = {}, pass = 'body') {
  const start = worldToScreen(pipe.start.x, pipe.start.z);
  const end = worldToScreen(pipe.end.x, pipe.end.z);
  const diameter = Math.max(1, pipe.diameter);
  const strokeWidth = Math.max(1, diameter * scale);
  const selected = Boolean(options.selected);

  if (pass === 'highlight') {
    if (!selected) return;
    svgEl('line', {
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      stroke: '#f59d13',
      'stroke-width': strokeWidth + 10,
      'stroke-linecap': 'round',
      opacity: 0.24,
      'pointer-events': 'none'
    }, parent);
    return;
  }

  if (pass === 'center') {
    svgEl('line', {
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      stroke: '#ffffff',
      'stroke-width': Math.max(1.4, Math.min(4, strokeWidth * 0.12)),
      'stroke-dasharray': `${Math.max(5, strokeWidth * 0.32)} ${Math.max(4, strokeWidth * 0.22)}`,
      'stroke-linecap': 'round',
      opacity: 0.9,
      'pointer-events': 'none'
    }, parent);
    return;
  }

  svgEl('line', {
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
    stroke: OBSTACLE_STYLE.pipe.stroke,
    'stroke-width': strokeWidth,
    'stroke-linecap': 'round',
    opacity: 0.92,
    'pointer-events': 'none'
  }, parent);
}

function drawPipeJoints2D(parent, joints = [], _pipes = [], optionsById = new Map(), scale = 1) {
  if (!joints.length) return;
  joints.forEach(joint => {
    const screen = worldToScreen(joint.x, joint.z);
    const related = joint.pipeIds || [];
    const selected = related.some(id => optionsById.get(id)?.selected);
    if (selected) {
      svgEl('circle', {
        cx: screen.x,
        cy: screen.y,
        r: Math.max(2, joint.radius * scale + 5),
        fill: '#f59d13',
        opacity: 0.24,
        'pointer-events': 'none'
      }, parent);
    }
    svgEl('circle', {
      cx: screen.x,
      cy: screen.y,
      r: Math.max(1, joint.radius * scale + 0.75),
      fill: OBSTACLE_STYLE.pipe.stroke,
      opacity: 0.94,
      'pointer-events': 'none'
    }, parent);
  });
}

function drawPipeLabel2D(parent, pipe) {
  const centerLine = [
    worldToScreen(pipe.start.x, pipe.start.z),
    worldToScreen(pipe.end.x, pipe.end.z)
  ];
  const midScreen = {
    x: (centerLine[0].x + centerLine[1].x) / 2,
    y: (centerLine[0].y + centerLine[1].y) / 2
  };
  svgEl('text', {
    x: midScreen.x + 8,
    y: midScreen.y - 10,
    fill: '#334155',
    'font-size': 12,
    'font-weight': 700,
    'pointer-events': 'none'
  }, parent).textContent = `Rohr Ø ${Math.round(pipe.diameter)}`;
}

function renderDuctNetwork2D(parent, ducts = [], optionsById = new Map()) {
  renderRectangularObstacleNetwork2D(parent, ducts, optionsById, {
    type: 'duct',
    className: 'duct',
    drawSegment: drawDuctObstacle2D,
    drawLabel: drawDuctLabel2D
  });
}

function renderRectangularObstacleNetwork2D(parent, obstacles = [], optionsById = new Map(), config = {}) {
  if (!parent || !Array.isArray(obstacles) || !obstacles.length || !config.type) return;
  const normalized = obstacles
    .map(obstacle => {
      const item = normalizeObstacle(obstacle);
      if (item) item.__renderOptions = optionsById.get(obstacle?.id) || optionsById.get(item.id) || {};
      return item;
    })
    .filter(obstacle => obstacle?.type === config.type && obstacleSegmentLength2D(obstacle) > 0.001);
  if (!normalized.length) return;

  const graph = buildObstacleSegmentConnectionGraph(normalized, { types: [config.type] });
  const visualSegments = buildRectangularObstacleRenderSegments(normalized, { types: [config.type] });
  const normalizedOptionsById = new Map(optionsById);
  normalized.forEach(obstacle => normalizedOptionsById.set(obstacle.id, obstacle.__renderOptions || {}));

  const bodyLayer = svgEl('g', { class: `${config.className}-body-layer` }, parent);
  const jointLayer = svgEl('g', { class: `${config.className}-joint-layer` }, parent);
  const labelLayer = svgEl('g', { class: `${config.className}-label-layer` }, parent);

  visualSegments.forEach(segment => {
    config.drawSegment(bodyLayer, segment, {
      ...obstacleRenderSegmentOptions(segment, normalizedOptionsById),
      showLabel: false
    });
  });
  drawRectangularObstacleJoints2D(
    jointLayer,
    (graph.joints || []).filter(joint => !joint.miterCorner),
    normalizedOptionsById
  );
  normalized.forEach(obstacle => config.drawLabel(labelLayer, obstacle, {
    ...(obstacle.__renderOptions || optionsById.get(obstacle.id) || {}),
    sourceObstacles: normalized
  }));
}

function obstacleRenderSegmentOptions(segment, optionsById = new Map()) {
  const ids = Array.isArray(segment?.sourceObstacleIds) && segment.sourceObstacleIds.length
    ? segment.sourceObstacleIds
    : Array.isArray(segment?.sourcePipeIds) && segment.sourcePipeIds.length
      ? segment.sourcePipeIds
      : segment?.id
        ? [segment.id]
        : [];
  return {
    selected: ids.some(id => optionsById.get(id)?.selected)
  };
}

function drawRectangularObstacleJoints2D(parent, joints = [], optionsById = new Map()) {
  if (!parent || !joints.length) return;
  const scale = Math.max(0.05, S.camera2D.scale || 1);
  joints.forEach(joint => {
    const screen = worldToScreen(joint.x, joint.z);
    const related = joint.pipeIds || [];
    const selected = related.some(id => optionsById.get(id)?.selected);
    const numericRadius = Number(joint.radius);
    const size = Math.max(2, (Number.isFinite(numericRadius) ? numericRadius : 1) * 2 * scale + 1.5);
    if (selected) {
      svgEl('rect', {
        x: screen.x - size / 2 - 4,
        y: screen.y - size / 2 - 4,
        width: size + 8,
        height: size + 8,
        fill: 'rgba(249,115,22,.18)',
        stroke: '#f59d13',
        'stroke-width': 2,
        'pointer-events': 'none'
      }, parent);
    }
    svgEl('rect', {
      x: screen.x - size / 2,
      y: screen.y - size / 2,
      width: size,
      height: size,
      fill: OBSTACLE_STYLE.ventilation.fill,
      stroke: OBSTACLE_STYLE.ventilation.stroke,
      'stroke-width': 2,
      'pointer-events': 'none'
    }, parent);
  });
}

function obstacleSegmentLength2D(obstacle) {
  if (!obstacle?.start || !obstacle?.end) return 0;
  return Math.hypot(obstacle.end.x - obstacle.start.x, obstacle.end.z - obstacle.start.z);
}

/**
 * Rendert draw Duct Obstacle2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} obstacle - Eingabewert für draw Duct Obstacle2 D.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawDuctObstacle2D(parent, obstacle, options = {}) {
  const selected = Boolean(options.selected);
  const polygon = options.worldPolygon || obstacle.__miterPolygons?.body || obstacleRectWorldPolygon(obstacle, obstacle.width);
  if (!polygon) return;
  const screenPoints = polygon.map(point => worldToScreen(point.x, point.z));
  const pointString = screenPoints.map(point => `${point.x},${point.y}`).join(' ');
  const color = OBSTACLE_STYLE.ventilation.stroke;
  const fill = OBSTACLE_STYLE.ventilation.fill;

  if (selected) {
    svgEl('polygon', {
      points: pointString,
      fill: 'rgba(249,115,22,.16)',
      stroke: '#f59d13',
      'stroke-width': 8,
      'stroke-linejoin': 'round',
      'pointer-events': 'none'
    }, parent);
  }

  svgEl('polygon', {
    points: pointString,
    fill,
    stroke: color,
    'stroke-width': 2.5,
    'stroke-linejoin': 'round',
    'pointer-events': 'none'
  }, parent);

  const centerLine = [
    worldToScreen(obstacle.start.x, obstacle.start.z),
    worldToScreen(obstacle.end.x, obstacle.end.z)
  ];
  svgEl('line', {
    x1: centerLine[0].x,
    y1: centerLine[0].y,
    x2: centerLine[1].x,
    y2: centerLine[1].y,
    stroke: '#ffffff',
    'stroke-width': 2,
    'stroke-dasharray': '9 6',
    opacity: 0.95,
    'pointer-events': 'none'
  }, parent);

  const midWorld = {
    x: (obstacle.start.x + obstacle.end.x) / 2,
    z: (obstacle.start.z + obstacle.end.z) / 2
  };
  const dx = obstacle.end.x - obstacle.start.x;
  const dz = obstacle.end.z - obstacle.start.z;
  const length = Math.hypot(dx, dz) || 1;
  const perp = { x: -dz / length, z: dx / length };
  const widthA = {
    x: midWorld.x + perp.x * obstacle.width / 2,
    z: midWorld.z + perp.z * obstacle.width / 2
  };
  const widthB = {
    x: midWorld.x - perp.x * obstacle.width / 2,
    z: midWorld.z - perp.z * obstacle.width / 2
  };
  const widthAScreen = worldToScreen(widthA.x, widthA.z);
  const widthBScreen = worldToScreen(widthB.x, widthB.z);
  svgEl('line', {
    x1: widthAScreen.x,
    y1: widthAScreen.y,
    x2: widthBScreen.x,
    y2: widthBScreen.y,
    stroke: OBSTACLE_STYLE.ventilation.stroke,
    'stroke-width': 1.5,
    'pointer-events': 'none'
  }, parent);
  if (options.showLabel === false) return;
  drawDuctLabel2D(parent, obstacle, options);
}

function drawDuctLabel2D(parent, obstacle, _options = {}) {
  const midWorld = {
    x: (obstacle.start.x + obstacle.end.x) / 2,
    z: (obstacle.start.z + obstacle.end.z) / 2
  };
  const midScreen = worldToScreen(midWorld.x, midWorld.z);
  svgEl('text', {
    x: midScreen.x + 8,
    y: midScreen.y - 10,
    fill: '#334155',
    'font-size': 12,
    'font-weight': 700,
    'pointer-events': 'none'
  }, parent).textContent = `Lüftung ${Math.round(obstacle.width)} x ${Math.round(obstacle.height)}`;
}

/**
 * Beschreibt obstacle Rect World Polygon im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} obstacle - Eingabewert für obstacle Rect World Polygon.
 * @param {*} width - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleRectWorldPolygon(obstacle, width) {
  const dx = obstacle.end.x - obstacle.start.x;
  const dz = obstacle.end.z - obstacle.start.z;
  const length = Math.hypot(dx, dz);
  if (length <= 0.001) return null;
  const perp = { x: -dz / length, z: dx / length };
  const half = Math.max(1, width / 2);
  return [
    { x: obstacle.start.x + perp.x * half, z: obstacle.start.z + perp.z * half },
    { x: obstacle.end.x + perp.x * half, z: obstacle.end.z + perp.z * half },
    { x: obstacle.end.x - perp.x * half, z: obstacle.end.z - perp.z * half },
    { x: obstacle.start.x - perp.x * half, z: obstacle.start.z - perp.z * half }
  ];
}

/**
 * Rendert draw Obstacle Interaction Overlay im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawObstacleInteractionOverlay() {
  drawObstacleTransformControls();
  const activeType = obstacleActiveTool();
  const drawState = S.ui?.obstacleDraw || null;
  if (activeType && drawState?.startPoint && drawState?.currentPoint) {
    const svg = getPlanSvg();
    const draft = normalizeObstacle({
      id: 'draft',
      type: activeType,
      start: drawState.startPoint,
      end: drawState.currentPoint,
      ...pendingObstacleConfig(activeType)
    });
    if (!draft) return;
    const layer = svgEl('g', { class: 'obstacle-draft-layer' }, svg);
    drawObstacle2D(layer, draft, { selected: true });
    applyGhostStyle(layer);
    drawObstacleDraftLengthControl(layer, draft, drawState);
    drawObstacleDraftAngleHint(layer, drawState);
    return;
  }
  if (S.drag?.type !== 'obstacleDraft' || !S.drag.startPoint || !S.drag.currentPoint) return;
  const svg = getPlanSvg();
  const type = S.drag.obstacleType;
  const draft = normalizeObstacle({
    id: 'draft',
    type,
    start: S.drag.startPoint,
    end: S.drag.currentPoint,
    ...pendingObstacleConfig(type)
  });
  if (!draft) return;
  const layer = svgEl('g', { class: 'obstacle-draft-layer' }, svg);
  drawObstacle2D(layer, draft, { selected: true });
    applyGhostStyle(layer);
  drawObstacleDraftLengthControl(layer, draft, { lengthInput: null });
}

function drawObstacleDraftLengthControl(parent, draft, drawState) {
  if (!parent || !draft?.start || !draft?.end) return;
  const start = worldToScreen(draft.start.x, draft.start.z);
  const end = worldToScreen(draft.end.x, draft.end.z);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const screenLength = Math.hypot(dx, dy);
  const worldLength = Math.hypot(draft.end.x - draft.start.x, draft.end.z - draft.start.z);
  if (screenLength <= 0.5 || worldLength <= 0.001) return;

  const input = drawState?.lengthInput?.active ? drawState.lengthInput : null;
  const label = input
    ? (input.value || 'Länge')
    : `${Math.round(worldLength)} mm`;
  const width = Math.max(72, Math.min(150, label.length * 8 + 24));
  const height = 28;
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const normal = { x: -dy / screenLength, y: dx / screenLength };
  const position = {
    x: mid.x - normal.x * 30,
    y: mid.y - normal.y * 30
  };
  let angle = Math.atan2(dy, dx) * 180 / Math.PI;
  if (angle > 90 || angle < -90) angle += 180;
  const group = svgEl('g', {
    transform: `translate(${position.x} ${position.y}) rotate(${angle})`,
    'pointer-events': 'none'
  }, parent);
  svgEl('rect', {
    x: -width / 2,
    y: -height / 2,
    width,
    height,
    rx: 5,
    fill: '#ffffff',
    stroke: input ? '#f59d13' : '#64748b',
    'stroke-width': input ? 2 : 1.2,
    opacity: 0.96
  }, group);
  svgEl('text', {
    x: 0,
    y: 5,
    'text-anchor': 'middle',
    fill: '#111827',
    'font-size': 13,
    'font-weight': input ? 700 : 600
  }, group).textContent = input ? `${label}|` : label;
}

function drawObstacleDraftAngleHint(parent, drawState) {
  const hint = drawState?.angleHint;
  const numericHintAngle = Number(hint?.angle);
  if (!parent || !hint?.position || !Number.isFinite(numericHintAngle)) return;
  const label = hint.label || `${Math.round(numericHintAngle)}\u00b0`;
  const anchor = worldToScreen(hint.position.x, hint.position.z);
  const width = Math.max(46, Math.min(86, label.length * 8 + 22));
  const height = 24;
  const group = svgEl('g', {
    transform: `translate(${anchor.x + 18} ${anchor.y - 22})`,
    'pointer-events': 'none'
  }, parent);
  svgEl('rect', {
    x: 0,
    y: -height,
    width,
    height,
    rx: 4,
    fill: '#111827',
    stroke: '#94a3b8',
    'stroke-width': 1,
    opacity: 0.88
  }, group);
  svgEl('text', {
    x: width / 2,
    y: -8,
    'text-anchor': 'middle',
    fill: '#f8fafc',
    'font-size': 12,
    'font-weight': 600
  }, group).textContent = label;
}

/**
 * Rendert draw Obstacle Transform Controls im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawObstacleTransformControls() {
  const obstacle = selectedObstacle();
  const svg = getPlanSvg();
  if (!obstacle || !svg || S.drag?.type === 'obstacleDraft') return;
  const centerWorld = obstacleCenter(obstacle);
  const center = worldToScreen(centerWorld.x, centerWorld.z);
  const angleDeg = Math.atan2(obstacle.end.z - obstacle.start.z, obstacle.end.x - obstacle.start.x) * 180 / Math.PI;
  const offset = obstacleScreenOffset({ x: 0, y: 54 }, angleDeg);
  drawObstacleTransformButton(svg, {
    x: center.x + offset.x,
    y: center.y + offset.y,
    iconRotation: angleDeg,
    obstacleId: obstacle.id
  });
}

/**
 * Beschreibt obstacle Screen Offset im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} offset - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @param {*} angleDeg - Winkel-/Rotationswert zur Ausrichtung im Plan oder Raum.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function obstacleScreenOffset(offset, angleDeg) {
  const numericAngle = Number(angleDeg);
  const rad = (Number.isFinite(numericAngle) ? numericAngle : 0) * Math.PI / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: offset.x * cos - offset.y * sin,
    y: offset.x * sin + offset.y * cos
  };
}

/**
 * Rendert draw Obstacle Transform Button im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt 2D-Darstellung und Hit-Tests.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawObstacleTransformButton(parent, options) {
  const btn = svgEl('g', { style: 'cursor:pointer' }, parent);
  svgEl('circle', {
    cx: options.x,
    cy: options.y,
    r: 16,
    fill: '#2563eb',
    stroke: '#1d4ed8',
    'stroke-width': 1.8
  }, btn);
  svgEl('text', {
    x: options.x,
    y: options.y + 5,
    'text-anchor': 'middle',
    'font-size': 16,
    'font-weight': 700,
    fill: '#ffffff',
    transform: `rotate(${Number.isFinite(Number(options.iconRotation)) ? Number(options.iconRotation) : 0} ${options.x} ${options.y})`,
    'pointer-events': 'none'
  }, btn).textContent = '↻';
  btn.setAttribute('data-obstacle-transform-action', 'rotate');
  btn.setAttribute('data-obstacle-id', options.obstacleId);
  btn.addEventListener('pointerdown', evt => {
    evt.stopPropagation();
    if (evt.pointerType === 'mouse' && evt.button !== 0) return;
    const finishPress = () => {
      cleanupPressListeners();
      clearInteractionTimer('obstacleRotateHold');
    };
    const cancelPress = () => {
      cleanupPressListeners();
      clearInteractionTimer('obstacleRotateHold');
    };
    function cleanupPressListeners() {
      window.removeEventListener('pointerup', finishPress, true);
      window.removeEventListener('pointercancel', cancelPress, true);
    }
    window.addEventListener('pointerup', finishPress, true);
    window.addEventListener('pointercancel', cancelPress, true);
    startObstacleRotatePress(evt, options.obstacleId);
  });
  btn.addEventListener('mousedown', stopCanvasMouseDown);
  btn.addEventListener('click', evt => {
    evt.stopPropagation();
    if (getInteractionFlag('suppressObstacleRotateClick')) {
      clearInteractionFlag('suppressObstacleRotateClick');
      return;
    }
    const obstacle = obstacleById(options.obstacleId);
    if (!obstacle) return;
    recordUndo('Hindernis drehen');
    rotateObstacle(obstacle.id, 90);
    selectObstacle(obstacle.id);
    setStatus('Hindernis 90° gedreht');
    AppRuntime.refresh(false);
  });
}

export {
  OBSTACLE_STYLE,
  drawObstacles2D,
  drawObstacle2D,
  drawPipeObstacle2D,
  renderPipeNetwork2D,
  pipeRenderSegmentOptions,
  drawPipeSegment2D,
  drawPipeJoints2D,
  drawPipeLabel2D,
  renderDuctNetwork2D,
  renderRectangularObstacleNetwork2D,
  obstacleRenderSegmentOptions,
  drawRectangularObstacleJoints2D,
  obstacleSegmentLength2D,
  drawDuctObstacle2D,
  drawDuctLabel2D,
  obstacleRectWorldPolygon,
  drawObstacleInteractionOverlay,
  drawObstacleDraftLengthControl,
  drawObstacleDraftAngleHint,
  drawObstacleTransformControls,
  obstacleScreenOffset,
  drawObstacleTransformButton
};
